import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';

export type VaultSecretState = {
  entity: string;
  exists: boolean;
  canRead: boolean;
  canWrite: boolean;
  access: string;
  secret?: string;
};

export interface VaultApi {
  getSecret(entityRef: string): Promise<VaultSecretState>;
  putSecret(entityRef: string, secret: string): Promise<void>;
  deleteSecret(entityRef: string): Promise<void>;
  /** Admin: delete catalog YAML + vault secret for a managed entity. */
  removeEntity(entityRef: string): Promise<void>;
}

export const vaultApiRef = createApiRef<VaultApi>({
  id: 'plugin.vault.service',
});

export class VaultClient implements VaultApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async baseUrl() {
    return this.discoveryApi.getBaseUrl('vault');
  }

  async getSecret(entityRef: string): Promise<VaultSecretState> {
    const base = await this.baseUrl();
    const res = await this.fetchApi.fetch(
      `${base}/secrets?entity=${encodeURIComponent(entityRef)}`,
    );
    if (!res.ok) {
      throw new Error(`Failed to load secret (${res.status})`);
    }
    return res.json();
  }

  async putSecret(entityRef: string, secret: string): Promise<void> {
    const base = await this.baseUrl();
    const res = await this.fetchApi.fetch(`${base}/secrets`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entity: entityRef, secret }),
    });
    if (!res.ok) {
      throw new Error(`Failed to save secret (${res.status})`);
    }
  }

  async deleteSecret(entityRef: string): Promise<void> {
    const base = await this.baseUrl();
    const res = await this.fetchApi.fetch(
      `${base}/secrets?entity=${encodeURIComponent(entityRef)}`,
      { method: 'DELETE' },
    );
    if (!res.ok) {
      throw new Error(`Failed to delete secret (${res.status})`);
    }
  }

  async removeEntity(entityRef: string): Promise<void> {
    const base = await this.baseUrl();
    const res = await this.fetchApi.fetch(
      `${base}/entities?entity=${encodeURIComponent(entityRef)}`,
      { method: 'DELETE' },
    );
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(
        body || `Failed to remove entity (${res.status})`,
      );
    }
  }
}
